import { useState } from 'react';
import {
  Badge,
  Collapse,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { roleAsDisplayName, statusAsDisplayName } from 'src/admin/users/utils';
import { formatDateTime } from 'src/meetings/utils/date-utils';
import { type CompanyUser, isPerson, useCompanyUsers } from 'src/secret-menu/hooks/useCompanyUsers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatRelativeDate } from 'src/utils/dateFormatting';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

const fullName = (user: CompanyUser) =>
  formatName(user.firstName, user.lastName, NameFormat.NATURAL);

/** Newest sign-in first; people who have not signed in since tracking began go last, by name. */
export function sortByLastLogin<T extends CompanyUser>(users: readonly T[]): T[] {
  return [...users].sort((a, b) => {
    if (a.lastLoginAt && b.lastLoginAt) return b.lastLoginAt.localeCompare(a.lastLoginAt);
    if (a.lastLoginAt) return -1;
    if (b.lastLoginAt) return 1;
    return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
  });
}

export default function FirmUsersCard({ companyId }: { companyId: string | undefined }) {
  const { users: data, isLoading, isError } = useCompanyUsers(companyId ?? null);
  const [open, setOpen] = useState(false);
  const users = sortByLastLogin((data ?? []).filter(isPerson));
  const latest = users.find(user => user.lastLoginAt);

  let subtitle = `${users.length} users · sign-ins are recorded from now on`;
  if (isLoading) subtitle = 'Loading users…';
  else if (isError) subtitle = 'Could not load users';
  else if (latest?.lastLoginAt) {
    subtitle = `${users.length} users · last sign-in ${fullName(latest)}, ${formatRelativeDate(latest.lastLoginAt)}`;
  }

  return (
    <Paper p="md" withBorder radius="md">
      <UnstyledButton
        w="100%"
        onClick={() => setOpen(current => !current)}
        aria-expanded={open}
        disabled={!companyId}
      >
        <Group gap="xs" wrap="nowrap" align="flex-start">
          {open ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          <Stack gap={2}>
            <Text fw={FONT_WEIGHTS.semibold}>Users</Text>
            <Text size="textXs" c="dimmed">
              {companyId ? subtitle : 'No billing record links this firm to a company'}
            </Text>
          </Stack>
        </Group>
      </UnstyledButton>
      <Collapse expanded={open}>
        <Stack mt="sm">
          {isLoading && <Loader size="sm" />}
          {isError && (
            <Text size="textSm" c="red">
              Could not load this firm&apos;s users.
            </Text>
          )}
          {!isLoading && !isError && companyId && (
            <Table verticalSpacing={6} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th ta="right">Last signed in</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map(user => (
                  <Table.Tr key={user.id}>
                    <Table.Td fw={FONT_WEIGHTS.semibold}>
                      {fullName(user)}
                      {user.userStatus !== 'ACTIVE' && (
                        <Badge size="xs" variant="light" color="gray" ml={6}>
                          {statusAsDisplayName(user.userStatus)}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="textSm" c="dimmed">
                        {user.emailAddress}
                      </Text>
                    </Table.Td>
                    <Table.Td>{roleAsDisplayName(user.userRole)}</Table.Td>
                    <Table.Td ta="right">
                      {user.lastLoginAt ? (
                        <Tooltip label={formatDateTime(user.lastLoginAt)} withArrow>
                          <Text size="textSm" span>
                            {formatRelativeDate(user.lastLoginAt)}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text size="textSm" c="dimmed" span>
                          Not since tracking began
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {users.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text size="textSm" c="dimmed">
                        No users.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}
